import { Fragment, useState } from "react";
import { ChevronDown, ChevronRight, Download } from "lucide-react";
import api from "../lib/api";

const n = (v) => Number(v || 0).toLocaleString("es-DO");
const rd = (v) => {
  const x = Number(v || 0);
  if (!x) return "—";
  return x >= 1e6 ? `RD$ ${(x / 1e6).toFixed(1)}M` : `RD$ ${(x / 1e3).toFixed(0)}K`;
};
const rateColor = (v) => (v >= 50 ? "text-emerald-700" : v >= 30 ? "text-amber-600" : "text-red-600");

function Cells({ r, showRate, showLeads, showAmounts, testid }) {
  return (
    <>
      {showLeads && <td className="py-1.5 px-1.5 text-right tabular-nums text-gray-700" data-testid={testid && `${testid}-leads`}>{n(r.leads)}</td>}
      <td className="py-1.5 px-1.5 text-right tabular-nums font-bold">{n(r.n)}</td>
      <td className="py-1.5 px-1.5 text-right tabular-nums text-emerald-700">{n(r.aprobado)}</td>
      <td className="py-1.5 px-1.5 text-right tabular-nums text-emerald-800">{n(r.firmado)}</td>
      <td className="py-1.5 px-1.5 text-right tabular-nums text-red-600">{n(r.rechazado)}</td>
      {showRate && <td className={`py-1.5 pl-1.5 text-right tabular-nums font-extrabold ${rateColor(r.approval_rate)}`}
                       data-testid={testid && `${testid}-rate`}>{r.approval_rate}%</td>}
      {showAmounts && (
        <>
          <td className="py-1.5 px-1.5 text-right tabular-nums font-bold whitespace-nowrap" data-testid={testid && `${testid}-monto-sometido`}
              title={`${n(r.con_monto)} cliente(s) con cotización`}>{rd(r.monto_sometido)}</td>
          <td className="py-1.5 px-1.5 text-right tabular-nums text-emerald-700 whitespace-nowrap" data-testid={testid && `${testid}-monto-aprobado`}>{rd(r.monto_aprobado)}</td>
          <td className="py-1.5 pl-1.5 text-right tabular-nums text-red-600 whitespace-nowrap" data-testid={testid && `${testid}-monto-rechazado`}>{rd(r.monto_rechazado)}</td>
        </>
      )}
    </>
  );
}

function Table({ title, rows, first, testid, showRate, showLeads, showAmounts, hint, expandable, action, wide }) {
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState(null);
  const visible = all ? rows : rows.slice(0, 10);
  const heads = [...(showLeads ? ["Leads"] : []), "Sometidas", "Aprob.", "Firm.", "Rech.", ...(showRate ? ["% Aprob."] : []),
                 ...(showAmounts ? ["RD$ Sometido", "RD$ Aprob.+Firm.", "RD$ Rech."] : [])];
  return (
    <div className={`rounded-xl border border-gray-200 bg-white p-3 sm:p-4 min-w-0 ${wide ? "lg:col-span-2" : ""}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-[13px] font-extrabold">{title}</div>
        {action}
      </div>
      {hint && <div className="text-[11px] text-gray-500 font-semibold -mt-1 mb-2">{hint}</div>}
      {!rows.length ? <div className="text-[12px] text-gray-400 font-semibold">Sin datos</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-[13px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b">
                <th className="text-left py-1.5 pr-2 font-extrabold">{first}</th>
                {heads.map((h) => <th key={h} className="text-right py-1.5 px-1.5 font-extrabold whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => {
                const isOpen = expandable && open === r.name;
                return (
                  <Fragment key={r.name}>
                    <tr className={`border-b last:border-0 hover:bg-gray-50 ${expandable ? "cursor-pointer" : ""}`}
                        onClick={expandable ? () => setOpen(isOpen ? null : r.name) : undefined}
                        data-testid={`${testid}-row-${i}`}>
                      <td className="py-1.5 pr-2 font-semibold text-gray-800 max-w-[140px] sm:max-w-[240px] truncate" title={r.name}>
                        <span className="inline-flex items-center gap-1">
                          {expandable && (isOpen ? <ChevronDown size={13}/> : <ChevronRight size={13}/>)}{r.name}
                        </span>
                      </td>
                      <Cells r={r} showRate={showRate} showLeads={showLeads} showAmounts={showAmounts} testid={`${testid}-row-${i}`}/>
                    </tr>
                    {isOpen && (r.brands || []).map((b, j) => (
                      <tr key={b.name} className="border-b bg-cyan-50/40" data-testid={`${testid}-row-${i}-brand-${j}`}>
                        <td className="py-1.5 pr-2 pl-6 text-gray-600 font-semibold truncate">{b.name}</td>
                        <Cells r={b} showRate={showRate} showLeads={showLeads} showAmounts={showAmounts}/>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {rows.length > 10 && (
            <button onClick={() => setAll((v) => !v)} data-testid={`${testid}-toggle`}
                    className="mt-2 text-xs font-bold text-cyan-700 hover:underline">
              {all ? "Ver solo top 10" : `Ver los ${rows.length}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ExportButton({ filters }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/sales-performance/bank-status/export-csv", { params: filters, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `estatus_financiamientos_${new Date().toISOString().slice(0, 10)}.csv` });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } finally { setBusy(false); }
  };
  return (
    <button onClick={download} disabled={busy} data-testid="sp-bank-unit-export"
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50 shrink-0"
            style={{ transition: "background-color 150ms ease" }}>
      <Download size={12}/> {busy ? "Exportando..." : "Exportar clientes"}
    </button>
  );
}

export default function BankBreakdownTables({ banks, units, filters, conMonto }) {
  return (
    <div className="grid gap-4 mt-6 lg:grid-cols-2">
      <Table title="Top 10 bancos" first="Banco" rows={banks} testid="sp-bank-top" showRate={false}/>
      <Table title="Tasa de aprobación por unidad de negocio" first="Unidad de negocio" rows={units} testid="sp-bank-unit"
             action={<ExportButton filters={filters}/>}
             showRate showLeads showAmounts expandable wide
             hint={`Unidad de negocio propietaria de la oportunidad · Haz clic para ver sus marcas · Leads según los filtros activos · RD$ = cotización más reciente del cliente (${n(conMonto)} clientes con monto)`}/>
    </div>
  );
}
